<i18n>
{
	"en": {
		"title": "Package Events",
		"noEvents": "There are no registered events.",
		"type": "Type",
		"date": "Date",
		"user": "User",
		"licencePlate": "Licence Plate",
		"location": "Location",
		"position":"Position",
		"received": "Received",
		"loaded": "Loaded",
		"collected": "Collected",
		"unloaded": "Unloaded",
		"delivered": "Delivered",
		"inbound": "Inbound",
		"outbound": "Outbound",
		"created": "Created",
		"rejected": "Rejected",
		"unsuccessful": "Unsuccessful pickup",
		"requested": "Requested",
		"accepted": "Accepted",
		"appSend": "Sent to Confirm",
		"info": "Information",
		"ediInstructionReceived": "Inbound EDI",
		"ediInstructionSent": "Outbound EDI",
		"ediTrackAndTraceSent" : "Outbound EDI"
	},
	"no": {
		"title": "Kolli hendelser",
		"noEvents": "Det er ingen registrerte hendelser.",
		"type": "Type",
		"date": "Dato",
		"user": "Bruker",
		"licencePlate": "Kollinummer",
		"location": "Lokasjon",
		"position":"Posisjon",
		"received": "Mottatt",
		"loaded": "Lastet",
		"collected": "Hentet",
		"unloaded": "Losset",
		"delivered": "Levert",
		"inbound": "Inngående",
		"outbound": "Utgående",
		"created": "Opprettet",
		"rejected": "Avslått",
		"unsuccessful": "Bomtur",
		"requested": "Forespurt",
		"accepted": "Godtatt",
		"appSend": "Sendt til Confirm",
		"info": "Informasjon",
		"ediInstructionReceived": "Inngående EDI",
		"ediInstructionSent": "Utgående EDI",
		"ediTrackAndTraceSent" : "Utgående EDI"

	}
}
</i18n>
<template>
	<div class="log">
		<div class="log-header">
			<t-panel-header
				:text="$t('title')"
				icon="history"
				type="bp"
				size="md"
			/>
		</div>
		<t-grid
			v-if="items && items.length > 0"
			:items="items"
			:columnDefinitions="columnDefinitions"
			:defaultColumnDefinition="defaultColumnDefinition"
			:rowHeight="40"
			:showFilters="false"
			:sort="sort"
			class="log-content"
		>
			<template v-slot:icon="{ item }">
				<t-timpex-icon
					:icon="getIcon(item.type)"
					type="bp"
					color="blue"
					style="padding-left: 3px;"
				/>
			</template>
			<template v-slot:type="{ item }">
				{{$t(item.type)}}
			</template>
			<template v-slot:location="{ displayValue }">
				<div v-if="displayValue" style="height: 16px;">{{ displayValue.name }}</div>
				<div v-if="displayValue" class="grid-cell-sub-line" :class="{'missing-geo-data': !displayValue.latitude || !displayValue.longitude }">{{ displayValue.street }}, {{ displayValue.postalCode }} {{ displayValue.postalPlace }}</div>
			</template>
			<template v-slot:info="{ item }">
				{{ ($i18n.locale==='nb-NO' || $i18n.locale==='nn-NO' || $i18n.locale==='no')
					? (item.info || item.infoEn)
					: (item.infoEn || item.info) }}
			</template>
			<template v-slot:date="{ item }">
				{{getDate(item.date)}}
			</template>
			<template v-slot:clerk="{ displayValue }">
				<div v-if="displayValue" style="height: 16px;">
					<template v-if="displayValue.name">
						{{displayValue.name}}
					</template>
					<template v-else-if="displayValue.email">
						{{displayValue.email}}
					</template>
					<template v-else-if="displayValue.phoneNumber">
						{{displayValue.phoneNumber}}
					</template>
				</div>
				<div v-if="displayValue && displayValue.name" class="grid-cell-sub-line">
					<template v-if="displayValue.email">
						{{displayValue.email}}
					</template>
					<template v-else-if="displayValue.phoneNumber">
						{{displayValue.phoneNumber}}
					</template>
				</div>
			</template>
			<template v-slot:licencePlate="{ item }">
				<span class="waybill-number">{{item.licencePlate}}</span>
			</template>
			<template v-slot:position="{ item }">
				<t-icon-button
					icon="map-marker"
					type="bp"
					appearance="minimal"
					disableBoxShadow
					:disabled="eventHasNoPosition(item)"
					@click="openPositionInGoogleMaps(item)"
				/>
			</template>
		</t-grid>
		<t-no-content-panel
			v-else
			:text="$t('noEvents')"
			icon="history"
			iconType="bp"
			class="log-content"
			noBetweenGap
		/>
	</div>
</template>

<script>
import moment from 'moment';
import { eventIcon, storageDateFormat } from '@/core';

export default {
	name: 'PackageEvents',
	props: { order: Object },
	data() {
		return { defaultColumnDefinition: { resizable: false, }, showGrid: false, sort: [{ field: 'date', direction: 'desc' }] };
	},
	computed: {
		items() {
			let events = this.order.packageEvents;
			for (let event of events) {
				let expectedEvents = this.order.numberOfPackages;
				let existingEvents = this.order.packageEvents.filter(x => x.type === event.type && x.location.name === event.location.name);
				if (existingEvents.length !== expectedEvents)
					event.activeRowWarning = true;
			}
			return events;
		},
		columnDefinitions() {
			return [
				{
					field: 'icon',
					displayName: '',
					width: 50,
				},
				{
					field: 'type',
					displayName: this.$t('type'),
					minWidth: 100,
					width: 100,
					resizable: true,
				},
				{
					field: 'location',
					valueGetter: item => item.location,
					displayName: this.$t('location'),
					minWidth: 70,
					resizable: true,
				},
				{
					field: 'info',
					displayName: this.$t('info'),
					minWidth: 80,
					resizable: true,
				},
				{
					field: 'date',
					displayName: this.$t('date'),
					width: 120,
				},
				{
					field: 'clerk',
					valueGetter: item => item.clerk,
					displayName: this.$t('user'),
					width: 200,
				},
				{
					field: 'licencePlate',
					displayName: this.$t('licencePlate'),
					width: 180,
				},
				{
					field: 'position',
					displayName: this.$t('position'),
					width: 70,
				}
			];
		},
	},
	methods: {
		onClose() {
			this.showGrid = false;
			this.$emit('close');
		},
		getIcon(type) {
			return eventIcon.getIcon(type);
		},
		getDate(date) {
			return moment(date.date, storageDateFormat).format('DD.MM.YYYY HH:mm');
		},
		openPositionInGoogleMaps(item) {
			let url = `https://www.google.com/maps/search/?api=1&query=${item.latitude},${item.longitude}`;
			let win = window.open(url, '_blank');
			win.focus();
		},
		eventHasNoPosition(item) {
			return !item.latitude || !item.longitude;
		}
	}
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.log {
	height: 100%;
	min-height: inherit;
}

.log-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 15px;
	background: white;
}

.log-content {
	height: calc(100% - 60px);
	width: 100%;
	min-height: inherit;
}

.waybill-number {
	@extend %font-families-mono;
	@extend %text-small;

	display: block;
	color: get-color($text, 'muted');
	letter-spacing: 1.2px;
}
</style>
